/**
 * Compact post-completion summary tile for the case Overview/Timeline sidebar.
 * Shows pending count + nearest deadline; clicking it bounces the user to
 * the full Post-Completion tab. Hidden entirely when there are no items
 * yet so the sidebar isn't cluttered for cases pre-completion.
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Stamp, Clock, Warning, CheckCircle } from "@phosphor-icons/react";

const KIND_LABEL = {
  sdlt: "SDLT Return",
  ap1: "AP1 Land Registry",
  mr01: "MR01 Charge",
  redemption: "Mortgage Redemption",
  leasehold_notice: "Leasehold Notice",
};

export default function PostCompletionSummaryCard({ caseId, onJump }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    API.get(`/cases/${caseId}/post-completion`)
      .then(({ data }) => {
        if (!cancelled) setSummary({ ...data.summary, completion_date: data.completion_date, total: data.items.length });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [caseId]);

  if (!summary || !summary.total) return null;

  const overdue = summary.overdue || 0;
  const pending = summary.pending || 0;
  const nearest = summary.nearest_deadline;
  let daysCopy = null;
  if (nearest?.deadline_date) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(nearest.deadline_date); due.setHours(0, 0, 0, 0);
    const days = Math.round((due - today) / 86400000);
    daysCopy = days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "Due today" : `${days}d left`;
  }

  return (
    <button
      type="button"
      onClick={onJump}
      data-testid="pc-overview-tile"
      className="w-full text-left bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:border-[#1462a0] hover:shadow-md transition-all"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Stamp weight="duotone" className="w-4 h-4 text-[#1462a0]" />
          <h4 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Post-Completion</h4>
        </div>
        {overdue > 0 ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600">
            <Warning weight="bold" className="w-3 h-3" /> {overdue} overdue
          </span>
        ) : pending === 0 ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600">
            <CheckCircle weight="bold" className="w-3 h-3" /> All done
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <Stat label="Pending" value={pending} tone={pending > 0 ? "amber" : "slate"} />
        <Stat label="Completed" value={summary.completed || 0} tone="emerald" />
      </div>
      {nearest && (
        <div className="border-t border-slate-100 pt-2 mt-2 flex items-center gap-1.5 text-[11px]">
          <Clock weight="bold" className="w-3 h-3 text-slate-400" />
          <span className="text-slate-600 truncate">
            Next: <span className="font-semibold text-slate-900">{KIND_LABEL[nearest.kind] || nearest.kind}</span>
            {daysCopy && <span className="ml-1 text-slate-500">· {daysCopy}</span>}
          </span>
        </div>
      )}
    </button>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = {
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    slate: "bg-slate-50 text-slate-700",
  }[tone] || "bg-slate-50 text-slate-700";
  return (
    <div className={`rounded-md p-2 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-bold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    </div>
  );
}
